/**
 * CommandPalette Component
 *
 * A keyboard-first command palette (Cmd/Ctrl+K) for quick navigation and actions.
 * Follows macOS Spotlight / VS Code command palette patterns.
 *
 * Features:
 * - Opens with Cmd/Ctrl+K global shortcut
 * - Fuzzy search across commands
 * - Keyboard navigation (arrows, enter, escape)
 * - Categories: Navigation, Actions, Help
 * - Journey-stage gating for premium features
 * - Recent commands tracking
 *
 * @module components/ui/CommandPalette
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  FolderGit2,
  Sparkles,
  RefreshCw,
  Settings,
  HelpCircle,
  Keyboard,
  LogOut,
  FileText,
  Plus,
  Share2,
  Download,
  Moon,
  Sun,
  Command,
  ArrowRight,
  Clock,
  Zap,
  X,
} from 'lucide-react';
import { useAuth } from '../auth';
import { useFocusTrap, useArrowNavigation } from '../../hooks/useKeyboardNavigation';
import { useLiveAnnounce } from './LiveRegion';

/**
 * Command categories
 */
const COMMAND_CATEGORIES = {
  RECENT: 'recent',
  NAVIGATION: 'navigation',
  ACTIONS: 'actions',
  HELP: 'help',
};

/**
 * Default commands available in the palette
 */
const getDefaultCommands = (user, isAdmin, navigate, actions) => [
  // Navigation
  {
    id: 'nav-dashboard',
    label: 'Go to Dashboard',
    description: 'View your main dashboard',
    category: COMMAND_CATEGORIES.NAVIGATION,
    icon: LayoutDashboard,
    keywords: ['home', 'main', 'overview'],
    action: () => navigate('/dashboard'),
    shortcut: 'G D',
  },
  {
    id: 'nav-settings',
    label: 'Go to Settings',
    description: 'Manage your account settings',
    category: COMMAND_CATEGORIES.NAVIGATION,
    icon: Settings,
    keywords: ['preferences', 'account', 'config'],
    action: () => navigate('/settings/subscription'),
    shortcut: 'G S',
    requiresAuth: true,
  },

  // Actions
  {
    id: 'action-import',
    label: 'Import Repositories',
    description: 'Import new repositories from GitHub, GitLab, or Bitbucket',
    category: COMMAND_CATEGORIES.ACTIONS,
    icon: Plus,
    keywords: ['add', 'github', 'gitlab', 'bitbucket', 'repo', 'sync'],
    action: () => actions?.openImport?.(),
    shortcut: 'I',
    requiresAuth: true,
  },
  {
    id: 'action-sync',
    label: 'Sync All Repositories',
    description: 'Refresh data from all connected sources',
    category: COMMAND_CATEGORIES.ACTIONS,
    icon: RefreshCw,
    keywords: ['refresh', 'update', 'reload'],
    action: () => actions?.syncAll?.(),
    shortcut: 'R',
    requiresAuth: true,
  },
  {
    id: 'action-analyze',
    label: 'Run Analysis',
    description: 'Start AI analysis on your portfolio',
    category: COMMAND_CATEGORIES.ACTIONS,
    icon: Sparkles,
    keywords: ['ai', 'llm', 'insights', 'skills'],
    action: () => actions?.runAnalysis?.(),
    requiresAuth: true,
    isAdmin: true,
  },
  {
    id: 'action-share',
    label: 'Share Portfolio',
    description: 'Create a shareable link to your portfolio',
    category: COMMAND_CATEGORIES.ACTIONS,
    icon: Share2,
    keywords: ['link', 'public', 'export'],
    action: () => actions?.sharePortfolio?.(),
    requiresAuth: true,
  },
  {
    id: 'action-export',
    label: 'Export Data',
    description: 'Download your portfolio data as PDF or JSON',
    category: COMMAND_CATEGORIES.ACTIONS,
    icon: Download,
    keywords: ['pdf', 'json', 'download', 'backup'],
    action: () => actions?.exportData?.(),
    requiresAuth: true,
  },

  // Help
  {
    id: 'help-shortcuts',
    label: 'Keyboard Shortcuts',
    description: 'View all available keyboard shortcuts',
    category: COMMAND_CATEGORIES.HELP,
    icon: Keyboard,
    keywords: ['keys', 'hotkeys', 'bindings'],
    action: () => actions?.showShortcuts?.(),
    shortcut: '?',
  },
  {
    id: 'help-docs',
    label: 'Documentation',
    description: 'Open help documentation',
    category: COMMAND_CATEGORIES.HELP,
    icon: FileText,
    keywords: ['guide', 'manual', 'faq'],
    action: () => window.open('/docs', '_blank'),
  },
  {
    id: 'help-support',
    label: 'Contact Support',
    description: 'Get help from our support team',
    category: COMMAND_CATEGORIES.HELP,
    icon: HelpCircle,
    keywords: ['contact', 'email', 'feedback'],
    action: () => window.open('mailto:support@portfolistic.com', '_blank'),
  },
  {
    id: 'help-tour',
    label: 'Restart Getting Started Tour',
    description: 'Replay the post-import guided tour',
    category: COMMAND_CATEGORIES.HELP,
    icon: RefreshCw,
    keywords: ['tour', 'onboarding', 'guide', 'walkthrough', 'tutorial'],
    action: () => {
      try { localStorage.removeItem('portfolistic_post_import_tour_done'); } catch {}
      window.location.reload();
    },
  },

  // Account
  {
    id: 'account-logout',
    label: 'Sign Out',
    description: 'Sign out of your account',
    category: COMMAND_CATEGORIES.ACTIONS,
    icon: LogOut,
    keywords: ['logout', 'exit'],
    action: () => actions?.signOut?.(),
    requiresAuth: true,
  },
];

/**
 * Fuzzy match score for search
 */
const fuzzyMatch = (query, text) => {
  if (!query) return 1;
  const lowerQuery = query.toLowerCase();
  const lowerText = text.toLowerCase();

  // Exact match gets highest score
  if (lowerText.includes(lowerQuery)) {
    return lowerText.startsWith(lowerQuery) ? 1 : 0.9;
  }

  // Fuzzy matching
  let queryIndex = 0;
  for (let i = 0; i < lowerText.length && queryIndex < lowerQuery.length; i++) {
    if (lowerText[i] === lowerQuery[queryIndex]) {
      queryIndex++;
    }
  }

  return queryIndex === lowerQuery.length ? 0.5 : 0;
};

/**
 * Search commands with fuzzy matching
 */
const searchCommands = (commands, query) => {
  if (!query.trim()) return commands;

  return commands
    .map(cmd => {
      const labelScore = fuzzyMatch(query, cmd.label);
      const descScore = fuzzyMatch(query, cmd.description) * 0.7;
      const keywordScore = cmd.keywords?.some(k =>
        k.toLowerCase().includes(query.toLowerCase())
      ) ? 0.8 : 0;

      const score = Math.max(labelScore, descScore, keywordScore);
      return { ...cmd, score };
    })
    .filter(cmd => cmd.score > 0)
    .sort((a, b) => b.score - a.score);
};

/**
 * Command Palette Provider & Component
 */
export const CommandPaletteContext = React.createContext(null);

export const useCommandPalette = () => {
  const context = React.useContext(CommandPaletteContext);
  if (!context) {
    return { open: () => {}, close: () => {}, isOpen: false };
  }
  return context;
};

/**
 * CommandPalette Component
 */
const CommandPalette = ({
  commands: customCommands,
  isOpen: controlledOpen,
  onOpenChange,
  actions = {},
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [recentCommands, setRecentCommands] = useState([]);
  const inputRef = useRef(null);

  const { user, signOut, isSuperadmin, isOwner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const announce = useLiveAnnounce();

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  const isAdmin = user && (isSuperadmin() || isOwner());

  // Merge custom commands with defaults
  const allCommands = useMemo(() => {
    const defaults = getDefaultCommands(user, isAdmin, navigate, {
      ...actions,
      signOut: () => {
        signOut();
        navigate('/');
      },
    });

    // Filter commands based on auth and admin status
    const filtered = defaults.filter(cmd => {
      if (cmd.requiresAuth && !user) return false;
      if (cmd.isAdmin && !isAdmin) return false;
      return true;
    });

    return customCommands ? [...customCommands, ...filtered] : filtered;
  }, [user, isAdmin, navigate, actions, signOut, customCommands]);

  // Filter and search commands
  const filteredCommands = useMemo(() => {
    let results = searchCommands(allCommands, query);

    // Add recent commands at the top if no query
    if (!query.trim() && recentCommands.length > 0) {
      const recentItems = recentCommands
        .map(id => allCommands.find(c => c.id === id))
        .filter(Boolean)
        .map(cmd => ({ ...cmd, category: COMMAND_CATEGORIES.RECENT }));

      // Remove duplicates from main results
      const recentIds = new Set(recentCommands);
      results = results.filter(c => !recentIds.has(c.id));

      return [...recentItems, ...results];
    }

    return results;
  }, [allCommands, query, recentCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups = {};
    filteredCommands.forEach(cmd => {
      const category = cmd.category || 'other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Get flat list for arrow navigation
  const flatCommands = useMemo(() => filteredCommands, [filteredCommands]);

  // Arrow navigation
  const { currentIndex, getItemProps, containerProps } = useArrowNavigation(
    flatCommands,
    {
      onSelect: (index, command) => executeCommand(command),
    }
  );

  // Focus trap
  const trapRef = useFocusTrap(isOpen, {
    initialFocus: 'input',
    onEscape: () => setIsOpen(false),
  });

  // Load recent commands from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('portfolistic-recent-commands');
      if (saved) {
        setRecentCommands(JSON.parse(saved).slice(0, 5));
      }
    } catch (e) {
      // Ignore localStorage errors
    }
  }, []);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setIsOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      announce('Command palette opened. Type to search commands.', 'polite');
    } else {
      setQuery('');
    }
  }, [isOpen, announce]);

  // Execute a command
  const executeCommand = useCallback((command) => {
    if (!command?.action) return;

    // Track in recents
    setRecentCommands(prev => {
      const updated = [command.id, ...prev.filter(id => id !== command.id)].slice(0, 5);
      try {
        localStorage.setItem('portfolistic-recent-commands', JSON.stringify(updated));
      } catch (e) {
        // Ignore localStorage errors
      }
      return updated;
    });

    setIsOpen(false);
    announce(`Executing: ${command.label}`, 'polite');

    // Execute after close animation
    setTimeout(() => command.action(), 100);
  }, [setIsOpen, announce]);

  // Handle input keydown
  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      containerProps.onKeyDown(e);
    }
    if (e.key === 'Enter' && flatCommands.length > 0) {
      e.preventDefault();
      executeCommand(flatCommands[currentIndex] || flatCommands[0]);
    }
  };

  // Category labels
  const categoryLabels = {
    [COMMAND_CATEGORIES.RECENT]: 'Recent',
    [COMMAND_CATEGORIES.NAVIGATION]: 'Navigation',
    [COMMAND_CATEGORIES.ACTIONS]: 'Actions',
    [COMMAND_CATEGORIES.HELP]: 'Help',
  };

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60"
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="flex min-h-full items-start justify-center p-4 pt-[15vh]">
        <div
          ref={trapRef}
          className="relative w-full max-w-xl transform overflow-hidden rounded-2xl
                     glass shadow-2xl
                     transition-all duration-200 ease-out"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-white/10">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Type a command or search..."
              className="flex-1 h-14 bg-transparent text-white placeholder-gray-500
                        focus:outline-none text-base"
              aria-label="Search commands"
              aria-activedescendant={flatCommands[currentIndex]?.id}
            />
            <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs
                           text-gray-500 bg-white/5 border border-white/10 rounded">
              esc
            </kbd>
          </div>

          {/* Results */}
          <div
            className="max-h-[60vh] overflow-y-auto py-2"
            role="listbox"
            {...containerProps}
          >
            {flatCommands.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No commands found for "{query}"
              </div>
            ) : (
              Object.entries(groupedCommands).map(([category, commands]) => (
                <div key={category} className="mb-2">
                  <div className="px-4 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {categoryLabels[category] || category}
                  </div>
                  {commands.map((command, idx) => {
                    const globalIndex = flatCommands.findIndex(c => c.id === command.id);
                    const isSelected = globalIndex === currentIndex;
                    const Icon = command.icon;

                    return (
                      <button
                        key={command.id}
                        id={command.id}
                        {...getItemProps(globalIndex)}
                        onClick={() => executeCommand(command)}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2.5 text-left
                          transition-colors cursor-pointer
                          ${isSelected
                            ? 'bg-primary-500/20 text-white'
                            : 'text-gray-300 hover:bg-white/5'
                          }
                        `}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <div className={`
                          p-1.5 rounded-lg
                          ${isSelected ? 'bg-primary-500/30' : 'bg-white/5'}
                        `}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{command.label}</div>
                          <div className="text-sm text-gray-500 truncate">
                            {command.description}
                          </div>
                        </div>
                        {command.shortcut && (
                          <kbd className="flex-shrink-0 px-2 py-0.5 text-xs text-gray-500
                                         bg-white/5 border border-white/10 rounded">
                            {command.shortcut}
                          </kbd>
                        )}
                        {isSelected && (
                          <ArrowRight className="w-4 h-4 text-primary-400 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer hints */}
          <div className="flex items-center justify-between px-4 py-2.5
                         border-t border-white/10 text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↓</kbd>
                <span>Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↵</kbd>
                <span>Select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/5 rounded">esc</kbd>
                <span>Close</span>
              </span>
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <Command className="w-3 h-3" />
              <span>Portfolistic</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render in portal to escape any parent overflow/z-index issues
  return createPortal(content, document.body);
};

CommandPalette.propTypes = {
  /** Custom commands to add to the palette */
  commands: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string,
      category: PropTypes.string,
      icon: PropTypes.elementType,
      keywords: PropTypes.arrayOf(PropTypes.string),
      action: PropTypes.func.isRequired,
      shortcut: PropTypes.string,
    })
  ),
  /** Controlled open state */
  isOpen: PropTypes.bool,
  /** Callback when open state changes */
  onOpenChange: PropTypes.func,
  /** Action handlers for built-in commands */
  actions: PropTypes.shape({
    openImport: PropTypes.func,
    syncAll: PropTypes.func,
    runAnalysis: PropTypes.func,
    sharePortfolio: PropTypes.func,
    exportData: PropTypes.func,
    showShortcuts: PropTypes.func,
  }),
};

/**
 * CommandPaletteProvider - Provides context for opening the palette from anywhere
 */
export const CommandPaletteProvider = ({ children, actions }) => {
  const [isOpen, setIsOpen] = useState(false);

  const value = useMemo(() => ({
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev),
  }), [isOpen]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        actions={actions}
      />
    </CommandPaletteContext.Provider>
  );
};

CommandPaletteProvider.propTypes = {
  children: PropTypes.node,
  actions: PropTypes.object,
};

export default CommandPalette;
